import { type PublicUserProfile, PublicUserProfileSchema } from "@fitties/shared";
import { firebaseFunctionsService } from "@infrastructure/firebase/firebaseFunctionsService";

/**
 * Fetch public profiles for the given UIDs via Cloud Function.
 * Returns only stripped public fields (no phone, likes, DOB, etc.).
 * Invalid rows are dropped; a bad envelope throws.
 */
export async function fetchPublicUserProfiles(uids: string[]): Promise<PublicUserProfile[]> {
  const unique = Array.from(new Set(uids.filter((uid) => typeof uid === "string" && uid.length > 0)));
  if (unique.length === 0) {
    return [];
  }

  return firebaseFunctionsService.callFunctionWithUsersList(
    "getPublicUserProfiles",
    { uids: unique },
    PublicUserProfileSchema
  );
}

/**
 * Fetch a single public profile by uid.
 * Returns null when the uid is missing from the response (including when the row was dropped as invalid).
 * Does not fall back to another row — that would return the wrong person.
 */
export async function fetchPublicUserProfile(uid: string): Promise<PublicUserProfile | null> {
  const users = await fetchPublicUserProfiles([uid]);
  return users.find((u) => u.uid === uid) ?? null;
}
